import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import { TextAreaWithHashtagsField } from '../../containers/Form';
import HashtagsWindow from '../../components/Inputs/TextAreaWithHashtags/HashTagsWindow';
import Strings from '../../Strings';
import { hasPermission, PERMISSIONS } from '../../utils/userPermissions';

function MessageTextArea({
  group,
  type,
  threshold = null,
  thresholdIdx = null,
  schedule,
  onScheduleChange,
  defaultReminders,
  validationErrors,
  highlightInvalidFields,
  prefabReplies = [],
}) {
  const showPrefabs = hasPermission(PERMISSIONS.MESSAGES_PREFABS);
  
  // Create refs for this specific message text area instance
  const remindersRef = useRef(null);
  const tooltipsRef = useRef(null);
  const tooltipsPrefabsRef = useRef(null);
  
  const isThresholdMessage = thresholdIdx !== null;
  const isTimeMilestoneMessage = type.startsWith('time_');
  const baseType = isThresholdMessage ? type.split('_threshold_')[0] : type;
  
  let textValue;
  let defaultReminderType;
  
  if (isTimeMilestoneMessage) {
    const [, day, complianceType] = type.split('_');
    textValue = schedule?.milestone_reminders?.milestone_rules?.time?.messages?.[day]?.[complianceType];
    defaultReminderType = 'time_milestone_messages';
  } else if (isThresholdMessage) {
    textValue = schedule?.milestone_reminders?.milestone_rules?.streak?.messages?.[threshold];
    defaultReminderType = 'streak_achievement';
  } else {
    textValue = schedule?.[group]?.[type]?.text;
    defaultReminderType = type;
  }

  const onReminderChange = (reminderGroup, reminderType, field, value) => {
    onScheduleChange(s => ({
      ...s,
      [reminderGroup]: {
        ...s[reminderGroup],
        [reminderType]: {
          ...s[reminderGroup]?.[reminderType],
          [field]: value,
        },
      },
    }));
  };

  const onTagChosen = (chosenGroup, chosenType, text, isPrefab = false, replace = false) => {
    if (!remindersRef.current) return;
    
    const { selectionStart, selectionEnd, value } = remindersRef.current;
    const textBeforeCursorPosition = value.substring(0, selectionStart);
    const textAfterCursorPosition = value.substring(selectionEnd, value.length);
    
    // Handle threshold-specific text changes
    const isChosenThresholdMessage = chosenType.includes('_threshold_');
    const isChosenTimeMilestoneMessage = chosenType.startsWith('time_');
    
    if (isChosenThresholdMessage) {
      const chosenThreshold = chosenType.split('_threshold_')[1];
      const currentThresholds = schedule?.milestone_reminders?.streak_achievement?.thresholds || [];
      const thresholdValue = currentThresholds[parseInt(chosenThreshold, 10)];
      onScheduleChange(s => ({
        ...s,
        milestone_reminders: {
          ...s.milestone_reminders,
          milestone_rules: {
            ...s.milestone_reminders?.milestone_rules,
            streak: {
              ...s.milestone_reminders?.milestone_rules?.streak,
              messages: {
                ...s.milestone_reminders?.milestone_rules?.streak?.messages,
                [thresholdValue]: replace ? text : `${textBeforeCursorPosition}${text}${textAfterCursorPosition}`
              }
            }
          }
        }
      }));
    } else if (isChosenTimeMilestoneMessage) {
      const [, day, complianceType] = chosenType.split('_');
      onScheduleChange(s => ({
        ...s,
        milestone_reminders: {
          ...s.milestone_reminders,
          milestone_rules: {
            ...s.milestone_reminders?.milestone_rules,
            time: {
              ...s.milestone_reminders?.milestone_rules?.time,
              messages: {
                ...s.milestone_reminders?.milestone_rules?.time?.messages,
                [day]: {
                  ...s.milestone_reminders?.milestone_rules?.time?.messages?.[day],
                  [complianceType]: replace ? text : `${textBeforeCursorPosition}${text}${textAfterCursorPosition}`
                }
              }
            }
          }
        }
      }));
    } else {
      onReminderChange(chosenGroup, chosenType, 'text', replace ? text : `${textBeforeCursorPosition}${text}${textAfterCursorPosition}`);
    }

    if (isPrefab) {
      tooltipsPrefabsRef.current?.close();
    } else {
      tooltipsRef.current?.close();
    }
    setTimeout(() => {
      if (remindersRef.current) {
        remindersRef.current.focus();
        remindersRef.current.selectionStart = selectionStart + text.length;
        remindersRef.current.selectionEnd = selectionStart + text.length;
      }
    }, 300);
  };

  const isTextDefault = (messageGroup, messageType, timeMilestoneType) => {
    if (messageType === 'time_milestone_messages') {
      const days = timeMilestoneType.split('_')[1];
      const complianceType = timeMilestoneType.split('_')[2];
      return schedule.milestone_reminders?.[messageGroup]?.time?.messages?.[days]?.[complianceType] === defaultReminders?.time_milestone_messages?.[days]?.[complianceType];
    }
    return schedule[messageGroup]?.[messageType]?.text === defaultReminders?.[messageType]?.message;
  };

  const handleTextChange = (newValue) => {
    if (isTimeMilestoneMessage) {
      const [, day, complianceType] = type.split('_');
      onScheduleChange(s => ({
        ...s,
        milestone_reminders: {
          ...s.milestone_reminders,
          milestone_rules: {
            ...s.milestone_reminders?.milestone_rules,
            time: {
              ...s.milestone_reminders?.milestone_rules?.time,
              messages: {
                ...s.milestone_reminders?.milestone_rules?.time?.messages,
                [day]: {
                  ...s.milestone_reminders?.milestone_rules?.time?.messages?.[day],
                  [complianceType]: newValue
                }
              }
            }
          }
        }
      }));
    } else if (isThresholdMessage) {
      onScheduleChange(s => ({
        ...s,
        milestone_reminders: {
          ...s.milestone_reminders,
          milestone_rules: {
            ...s.milestone_reminders?.milestone_rules,
            streak: {
              ...s.milestone_reminders?.milestone_rules?.streak,
              messages: {
                ...s.milestone_reminders?.milestone_rules?.streak?.messages,
                [threshold]: newValue
              }
            }
          }
        }
      }));
    } else {
      onReminderChange(group, type, 'text', newValue);
    }
  };

  const handleResetToDefault = () => {
    if (isTimeMilestoneMessage) {
      const [, day, complianceType] = type.split('_');
      onScheduleChange(s => ({
        ...s,
        milestone_reminders: {
          ...s.milestone_reminders,
          milestone_rules: {
            ...s.milestone_reminders?.milestone_rules,
            time: {
              ...s.milestone_reminders?.milestone_rules?.time,
              messages: {
                ...s.milestone_reminders?.milestone_rules?.time?.messages,
                [day]: {
                  ...s.milestone_reminders?.milestone_rules?.time?.messages?.[day],
                  [complianceType]: defaultReminders?.time_milestone_messages?.[day]?.[complianceType]
                }
              }
            }
          }
        }
      }));
    } else if (isThresholdMessage) {
      onScheduleChange(s => ({
        ...s,
        milestone_reminders: {
          ...s.milestone_reminders,
          milestone_rules: {
            ...s.milestone_reminders?.milestone_rules,
            streak: {
              ...s.milestone_reminders?.milestone_rules?.streak,
              messages: {
                ...s.milestone_reminders?.milestone_rules?.streak?.messages,
                [threshold]: defaultReminders?.[defaultReminderType]?.message
              }
            }
          }
        }
      }));
    } else {
      onReminderChange(group, type, 'text', defaultReminders?.[type]?.message);
    }
  };

  return (
    <div className="reminder-text">
      <TextAreaWithHashtagsField
        value={textValue}
        onChange={handleTextChange}
        label={isTimeMilestoneMessage ? 
          `Message after ${type.split('_')[1]} days - ${type.split('_')[2]} achievement` : 
          isThresholdMessage ? `Message for ${threshold} ${threshold === 1 ? 'day' : 'days'} streak` : ""}
        rows={5}
        hashtags={
          defaultReminders?.[defaultReminderType]?.actions?.length > 0
            ? defaultReminders?.[defaultReminderType]?.actions.map(a => {
              return {
                id: a,
                display: a,
                shortcut: a,
                title: a,
              };
            })
            : []
        }
        trigger={/(\[(\w*))$/}
        markup="[__id__]"
        displayTransform={(_, display) => {
          return `[${display}]`;
        }}
        regex={/\[(\S+?)\]/}
        simpleMenu
        tooltipForEntries={Strings.insertTag}
        inputRef={remindersRef}
        forceSuggestionsAboveCursor={false}
        isRequired
        errorsForTooltip={
          validationErrors.find(v => {
            if (isTimeMilestoneMessage) {
              const [, day, complianceType] = type.split('_');
              return v.property === 'milestone_rules' && v.type === 'time' && v.day === day && v.complianceType === complianceType;
            } 
            if (isThresholdMessage) {
              return v.property === group && v.type === baseType && v.field === 'thresholdTexts';
            } 
            return v.property === group && v.type === type && v.field === 'text';
          })?.errors || []
        }
        highlightInvalid={highlightInvalidFields}
      />
      <div className="text-area-bottom-buttons">
        {!isTextDefault(group, isTimeMilestoneMessage ? 'time_milestone_messages' : isThresholdMessage ? baseType : type, isTimeMilestoneMessage ? type : undefined) && (
          <button
            type="button"
            onClick={handleResetToDefault}
            className="reset-reminder-text brand-white-gray"
          >
            {Strings.setDefault}
          </button>
        )}
        {defaultReminders?.[defaultReminderType]?.actions?.length > 0 && (
          <>
            <div className="help" data-tooltip-id={`tooltip-tags-${type}`}>
              ?
            </div>
            <ReactTooltip
              id={`tooltip-tags-${type}`}
              effect="solid"
              clickable
              variant="light"
              place="top-start"
              className="message-textbox__suggestions"
              border="1px solid #bababa"
              ref={tooltipsRef}
            >
              <div>
                <h3 style={{ marginBottom: '4px' }}>{Strings.patient_medications.tagsForReminders}</h3>
                <div>{Strings.patient_medications.clickOnTag}</div>
              </div>
              <HashtagsWindow
                onTagChosen={text => onTagChosen(group, type, text)}
                hashtags={defaultReminders?.[defaultReminderType]?.actions?.length > 0 ? defaultReminders?.[defaultReminderType]?.actions : []}
              />
            </ReactTooltip>
          </>
        )}
        {prefabReplies?.length > 0 && showPrefabs && (
          <>
            <div className="help" data-tooltip-id={`tooltip-prefabs-${type}`}>
              #
            </div>
            <ReactTooltip
              id={`tooltip-prefabs-${type}`}
              effect="solid"
              clickable
              variant="light"
              place="top-start"
              className="message-textbox__suggestions"
              border="1px solid #bababa"
              ref={tooltipsPrefabsRef}
            >
              <h3>Choose prefab message</h3>

              <HashtagsWindow
                onTagChosen={text => onTagChosen(group, type, text, true)}
                onReplace={text => onTagChosen(group, type, text, true, true)}
                hashtags={prefabReplies?.length > 0 ? prefabReplies.map(pr => pr.full_txt) : []}
                noBrackets
              />
            </ReactTooltip>
          </>
        )}
      </div>
    </div>
  );
}

MessageTextArea.propTypes = {
  group: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  threshold: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  thresholdIdx: PropTypes.number,
  schedule: PropTypes.object.isRequired,
  onScheduleChange: PropTypes.func.isRequired,
  defaultReminders: PropTypes.object.isRequired,
  validationErrors: PropTypes.array.isRequired,
  highlightInvalidFields: PropTypes.bool.isRequired,
  prefabReplies: PropTypes.array,
};

export default MessageTextArea;